import { useState, useRef, useMemo } from "react";
import Button from "@syntensor/common/components/button";
import { ICompoundDefinition } from "@syntensor/common/types";
import getCopy from "@syntensor/common/data/copy";
import Modal from "react-modal";

import styles from "./compounds.module.css";
import Table, {
  deleteCellRenderer,
  TableRef,
} from "@syntensor/common/components/table";
import { TableConfig } from "./tableConfig";

export interface ICreateNewStudyCompoundsFormProps {
  title: string;
  editButtonTitle: string;
  editTitle?: string;
  onChange: (compounds: ICompoundDefinition[]) => void;
  compounds: ICompoundDefinition[];
  isDisabled?: boolean;
  tableConfig: TableConfig;
}

export default function CreateNewStudyCompoundsForm({
  title,
  editButtonTitle,
  editTitle,
  onChange,
  compounds = [],
  tableConfig = { cols: [] },
}: ICreateNewStudyCompoundsFormProps) {
  const [showModal, setShowModal] = useState(false);
  const modalTableRef = useRef<TableRef>(null);
  const tableRef = useRef<TableRef>(null);

  const showSubtext = useMemo(() => {
    return tableConfig.cols.find((col) => col.id === "nctid");
  }, []);

  Modal.setAppElement("#root");

  const back = () => {
    setShowModal(false);
  };

  const saveChanges = () => {
    let validData = true;

    if (modalTableRef.current) {
      const newCompounds = modalTableRef.current.getInternalState();
      const frequency = new Map();

      for (const datum of newCompounds) {
        const firstField = datum[Object.keys(datum)[0]];
        const count = frequency.get(firstField) || 0;
        frequency.set(firstField, count + 1);

        if (frequency.get(firstField) > 1) {
          console.dir(frequency);
          validData = false;
          break;
        }

        for (const key in datum) {
          if (key.startsWith("invalid") && datum[key] === true) {
            validData = false;
            break;
          }
        }
        if (!validData) break;
      }

      if (validData) {
        tableRef.current?.setInternalState(newCompounds);
        const data = newCompounds.map((record) =>
          Object.entries(record).reduce((filteredRecord: any, [key, value]) => {
            if (
              value !== undefined &&
              value !== null &&
              !key.startsWith("invalid") &&
              !key.startsWith("selectCellData") &&
              !key.startsWith("validating")
            ) {
              filteredRecord[key] = value;
            }
            return filteredRecord;
          }, {})
        ) as ICompoundDefinition[];
        onChange(data);
      }
    }

    if (validData) {
      setShowModal(false);
    }
  };

  // bit hacky ??
  const openTableEditor = () => {
    if (tableRef.current) {
      const newCompounds = tableRef.current.getInternalState();
      setShowModal(true);

      const trySetModalState = () => {
        if (modalTableRef.current) {
          modalTableRef.current.setInternalState(newCompounds);
        } else {
          setTimeout(trySetModalState, 25);
        }
      };

      setTimeout(trySetModalState, 15);
    }
  };

  const editConfig = {
    ...tableConfig,
    allowEditing: true,
    cols: [
      ...tableConfig.cols,
      {
        id: "delete",
        name: "Remove",
        width: "5%",
        cellRenderer: deleteCellRenderer,
      },
    ],
  };

  return (
    <div>
      <div className={styles.compoundFormSection}>
        <h3 className={styles.formTitle}>{title}</h3>

        {showSubtext && (
          <p
            style={{
              fontWeight: "normal",
              fontSize: "0.75em",
              marginBottom: "1em",
            }}
          >
            For compounds in our database, searching for a compound name will
            also supply the SMILES, mechanism of action, and gene symbol list.
            Similarly, for diseases in our database, searching for a disease
            name will also supply the EFO indication identifier. Clinical trial
            IDs are optional while the rest of the fields are required to
            generate an inference.
          </p>
        )}

        <Table ref={tableRef} config={tableConfig} data={compounds} />

        <div className={styles.compounds}>
          <Button
            className={styles.addCompoundBtn}
            onClick={openTableEditor}
            role="primary"
          >
            {editButtonTitle}
          </Button>
        </div>

        <Modal
          isOpen={showModal}
          onRequestClose={() => setShowModal(false)}
          contentLabel="Add component modal"
          style={{
            overlay: {
              backgroundColor: "var(--primary-light)",
              zIndex: 99999,
            },
            content: {
              backgroundColor: "var(--primary-light)",
              position: "absolute",
              top: "0%",
              left: "0%",
              width: "100%",
              height: "100%",
              padding: "0",
              border: "0",
            },
          }}
        >
          <div className={styles.newUserForm}>
            <div
              className={styles.titleWrapper}
              style={{ borderBottom: "1px solid var(--outline-gray)" }}
            >
              <h2 className={styles.title}>
                {getCopy("projects_project_configure")}{" "}
                {editTitle ? `/ ${editTitle}` : ""}
              </h2>

              <div className={styles.buttonsWrapper}>
                <Button role="subtle" onClick={back}>
                  {getCopy("studies_search_back")}
                </Button>

                <Button role="primary" onClick={saveChanges}>
                  {getCopy("studies_edit_save-changes")}
                </Button>
              </div>
            </div>

            <div className={styles.compoundForm} style={{ marginTop: "1rem" }}>
              <div className={styles.compoundFormSection}>
                <div style={{ paddingRight: "0.5rem" }}>
                  <h3 className={styles.formTitle}>{title}</h3>

                  {showSubtext && (
                    <p
                      style={{
                        fontWeight: "normal",
                        fontSize: "0.75em",
                        marginBottom: "1em",
                      }}
                    >
                      For compounds in our database, searching for a compound
                      name will also supply the SMILES, mechanism of action, and
                      gene symbol list. Similarly, for diseases in our database,
                      searching for a disease name will also supply the EFO
                      indication identifier. Clinical trial IDs are optional
                      while the rest of the fields are required to generate an
                      inference.
                    </p>
                  )}

                  <Table
                    ref={modalTableRef}
                    config={editConfig}
                    data={compounds}
                  />
                </div>
              </div>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
}
